import React, {
    useCallback,
    useContext,
    useEffect,
    useState
} from "react";
import axios from "axios";
import {
    Alert,
    Button,
    OverlayTrigger,
    Table,
    Tooltip
} from "react-bootstrap";
import PropTypes from "prop-types";

import ShopsContext from "../../../context/ShopsContext";
import Loading from "../../../components/Loading";
import DateFormatter from "../../../components/DateFormatter";
import formatSegmentLabel from "../../../utilities/formatSegmentLabel";
import AddSegmentBlockModal from "./modal/AddSegmentBlockModal";
import DeleteSegmentBlockModal from "./modal/DeleteSegmentBlockModal";

function getBlockSegmentLabel(block, segments) {
    if(!block.segmentId) {
        return "Hele dag";
    }
    const segment = segments ? segments.find(seg => seg.id === block.segmentId) : null;
    if(!segment) {
        return "Segment #" + block.segmentId;
    }
    return formatSegmentLabel(segment.startTime, segment.durationInMinutes);
}

function ProductDetailSegmentBlocks({ product }) {
    const shopsContext = useContext(ShopsContext);
    const [blocks, setBlocks] = useState(null);
    const [segments, setSegments] = useState(null);
    const [error, setError] = useState(null);
    const [showAddModal, setShowAddModal] = useState(false);
    const [blockToDelete, setBlockToDelete] = useState(null);

    const getBlocks = useCallback(async () => {
        if(!product) return;
        setBlocks(null);
        setError(null);
        try {
            const response = await axios.post("/getRentalProductSegmentBlocks", {
                shopId: shopsContext.currentShop.id,
                productId: product.id,
            });
            if(response.data.valid) {
                setBlocks(response.data.blocks.sort((blockA, blockB) => blockB.date.localeCompare(blockA.date)));
            } else {
                setError("Er ging iets fout. Probeer het later opnieuw. (" + response.data.error + ")");
            }
        } catch(requestError) {
            console.error(requestError);
            setError("Er ging iets fout. Probeer het later opnieuw.");
        }
    }, [shopsContext.currentShop.id, product]);

    const getSegments = useCallback(async () => {
        if(!product) return;
        setSegments(null);
        try {
            const response = await axios.post("/getRentalProductSegments", {
                shopId: shopsContext.currentShop.id,
                productId: product.id,
            });
            if(response.data.valid) {
                setSegments(response.data.segments);
            } else {
                setError("Er ging iets fout bij het ophalen van segmenten. (" + response.data.error + ")");
            }
        } catch(requestError) {
            console.error(requestError);
            setError("Er ging iets fout bij het ophalen van segmenten.");
        }
    }, [shopsContext.currentShop.id, product]);

    useEffect(() => {
        getBlocks();
        getSegments();
    }, [getBlocks, getSegments]);

    if(!product) {
        return <Loading/>;
    }

    return (
        <React.Fragment>
            { error && (
                <Alert variant="danger">{ error }</Alert>
            )}

            <div className="d-flex justify-content-between align-items-center mb-3">
                <h4 className="mb-0">Blokkeringen</h4>
                <OverlayTrigger overlay={
                    <Tooltip id="addBlock">Nieuwe blokkering</Tooltip>
                }>
                    <Button
                        variant="secondary"
                        size="sm"
                        onClick={ () => setShowAddModal(true) }
                    >
                        <i className="fas fa-fw fa-plus"/>
                    </Button>
                </OverlayTrigger>
            </div>

            <Table size="sm" className="results" hover={ blocks && blocks.length > 0 }>
                <thead className="thead-light">
                <tr>
                    <th className="text-left">Datum</th>
                    <th className="text-left">Segment</th>
                    <th className="text-center">Acties</th>
                </tr>
                </thead>
                <tbody>
                { blocks === null ? (
                    <tr>
                        <td colSpan={ 3 }>
                            <Loading/>
                        </td>
                    </tr>
                ) : blocks.length === 0 ? (
                    <tr>
                        <td colSpan={ 3 } className="text-center">
                            <h1><i className="fas fa-ban"/></h1>
                            <h4>Geen blokkeringen</h4>
                            <p>Er zijn nog geen blokkeringen aangemaakt voor dit product.</p>
                        </td>
                    </tr>
                ) : blocks.map((block) => (
                    <tr key={ block.id }>
                        <td className="align-middle">
                            <DateFormatter date={ block.date } format="DD-MM-YYYY"/>
                        </td>
                        <td className="align-middle">
                            { getBlockSegmentLabel(block, segments) }
                        </td>
                        <td className="align-middle text-center">
                            <Button
                                variant="outline-danger"
                                size="sm"
                                onClick={ () => setBlockToDelete(block) }
                            >
                                <i className="fas fa-trash"/>
                            </Button>
                        </td>
                    </tr>
                ))}
                </tbody>
            </Table>

            <AddSegmentBlockModal
                show={ showAddModal }
                handleClose={ () => setShowAddModal(false) }
                product={ product }
                segments={ segments }
                onAdded={ getBlocks }
            />

            <DeleteSegmentBlockModal
                show={ blockToDelete !== null }
                handleClose={ () => setBlockToDelete(null) }
                product={ product }
                block={ blockToDelete }
                onDeleted={ getBlocks }
            />
        </React.Fragment>
    );
}
ProductDetailSegmentBlocks.propTypes = {
    product: PropTypes.object,
}

export default React.memo(ProductDetailSegmentBlocks);
